import _ from "lodash";
import moment from "moment-timezone";

import SupabaseDocument from "data/supabase/document";
import { supabase } from "config/supabase";
import TugSession from "./tugSession";
import Snapshots from "data/supabase/snapshots";
import CurrentSession from "data/userSession";
import Coverage from "utils/coverage";
import { isValidString } from "utils/misc";
import { bustProfileUrl } from "utils/profilePhoto";
import { FIREBASE_CONFIG } from "config/app";
import Logger from "utils/logger";
import Method from "../method";
import TimeUtils from "utils/time";
import Streak from "utils/streak";
import { DEBUG_MOCK_UID } from "config/debug";
import { changedColumns, hasPayload } from "utils/rowDiff";

// User is now Supabase-backed (public `users` + owner-only `users_private`), like
// TugSession/Image. Reads come from the `users_public` view (public columns + streak
// from streak_scores + totals summed from snapshots); the signed-in user also reads
// their own `users_private` row. Any save upserts BOTH tables and flips
// `users.migrated = true` so the backend Firestore->Supabase mirror stops overwriting
// this user. The public API (getters/setters/methods) is unchanged from the Firestore
// version so no surface had to change.
export default class User extends SupabaseDocument {
  static COLLECTION = "users";

  constructor(uid = null) {
    super();
    this.id = uid;

    this.active = false;
    this._above18 = false;
    this.bio = null;
    this.deleted = false;
    this.emoji = null;
    this.extraSupportRequested = false;
    this.hasPublicImages = false;
    this.intact = false;
    this.incognito = false;
    this.streakDisabled = false;
    this.sfw = false; // SFW mode: blur all photos behind tap-to-reveal (private pref)
    this.supporter = false;
    this.dates = {
      joined: moment(),
      lastTugged: null,
      started: moment(),
      streakStart: null,
      lastSync: null,
    };
    this.kudos = { sent: 0, received: 0 };
    this.level = {
      start: {
        rci: false,
        value: null,
      },
      current: {
        rci: false,
        value: null,
      },
    };
    this.methods = {};
    this.preferences = {
      notifications_buddies: true,
      notifications_kudos: true,
      notifications_streak: true,
      notifications_messages: true,
      haptic: true,
    };
    this.favorites = [];
    this.spreadsheet = null;
    this.stats = {
      method_breakdown: {},
      days: 0,
      duration: 0,
      sessions: 0,
    };
    this._streak = 0;
    this.timezone = TimeUtils.getTimezone();
    this.username = {
      display: null,
      standardized: null,
    };
    this.location = null; // { city, lat, lon, formatted }
    // Key snapshots by the mock-aware uid, not the raw id, so DEBUG_MOCK_UID
    // emulation routes every snapshot-derived stat (history, progress, charts) to
    // the target user. this.id is set above, so this.uid resolves here.
    this.snapshot = new Snapshots(this.uid);
    // No baseline until load() reads the DB rows -> save() writes full rows to
    // create them (new user). changedColumns() treats null as "write everything".
    this._publicRowOriginal = null;
    this._privateRowOriginal = null;
    this.saveCheckpoint();
  }

  async activeTugSession() {
    return TugSession.active(this.uid);
  }

  async cacheMethod(method) {
    const category = await Method.getCategory(method);
    this.methods[method] = category;
  }

  static async doesUsernameExist(username) {
    // Case-insensitive match replaces the old username.standardized field.
    const { data, error } = await supabase
      .from("users")
      .select("id")
      .ilike("username", _.trim(username))
      .limit(1);
    if (error) throw error;
    return (data || []).length > 0;
  }

  get incompleteSignup() {
    if (
      !isValidString(this.username.standardized) ||
      !isValidString(this.username.display)
    ) {
      return "Your username is missing";
    }
    if (
      _.isEmpty(this.level) ||
      this.levelStart === null ||
      this.levelCurrent === null
    ) {
      return "Don't forget to add your CI levels";
    }
    return null;
  }

  get isNew() {
    return this.dates.joined.isSameOrAfter(moment().subtract(1, "month"));
  }

  // Snapshot used only for change detection (changesMade). Mirrors the persisted
  // fields; excludes derived/volatile values (streak, lastTugged, stats) so time
  // passing or a streak refresh never looks like an unsaved edit.
  get json() {
    return {
      above18: this._above18,
      bio: isValidString(this.bio) ? _.trim(this.bio) : null,
      deleted: this.deleted,
      emoji: this.emoji,
      extraSupportRequested: this.extraSupportRequested,
      hasPublicImages: this.hasPublicImages,
      intact: this.intact,
      incognito: this.incognito,
      streakDisabled: this.streakDisabled,
      sfw: this.sfw,
      supporter: this.supporter,
      dates: {
        joined: this.dates.joined
          ? this.dates.joined.format(TimeUtils.FullDateTimeString)
          : null,
        started: this.dates.started
          ? this.dates.started.format(TimeUtils.FullDateString)
          : null,
        streakStart: this.dates.streakStart
          ? this.dates.streakStart.format(TimeUtils.FullDateString)
          : null,
        lastSync: this.dates.lastSync
          ? moment(this.dates.lastSync).format(TimeUtils.FullDateTimeString)
          : null,
      },
      kudos: this.kudos,
      level: this.level,
      methods: this.methods,
      preferences: this.preferences,
      spreadsheet: isValidString(this.spreadsheet) ? this.spreadsheet : null,
      timezone: this.timezone,
      username: this.username,
      location: this.location,
      favorites: this.favorites,
    };
  }

  // snake_case row for public.users. id + uid added in save(). Always marks the user
  // migrated so the backend mirror yields ownership to Supabase.
  toRow() {
    return {
      username: this.username.display || null,
      joined: this.dates.joined ? this.dates.joined.toISOString() : null,
      started: this.dates.started
        ? this.dates.started.format(TimeUtils.FullDateString)
        : null,
      // Incognito forces photos private, enforced at the write chokepoint so the
      // invariant holds no matter which UI path saved (the feed's public_images
      // view and the Photos toggle both rely on it).
      has_public_images: this.incognito ? false : this.hasPublicImages,
      incognito: this.incognito,
      intact: this.intact,
      supporter: this.supporter,
      deleted: this.deleted,
      // extra_support_requested intentionally NOT written: the legacy user-doc
      // flag is being retired in favor of a dedicated community-support table.
      // (Still hydrated/read for now; just never persisted from here.)
      bio: isValidString(this.bio) ? _.trim(this.bio) : null,
      emoji: this.emoji,
      level_current: this.levelCurrent,
      level_start: this.levelStart,
      methods: this.methods,
      kudos: this.kudos,
      location: this.location,
      above18: this._above18,
      migrated: true,
    };
  }

  // snake_case row for public.users_private (owner-only). last_sync (syncer-owned)
  // and raw are intentionally omitted so merge-duplicates never clobbers them.
  toPrivateRow() {
    return {
      id: this.id,
      uid: this.uid,
      spreadsheet: isValidString(this.spreadsheet) ? this.spreadsheet : null,
      timezone: this.timezone,
      streak_disabled: this.streakDisabled,
      sfw: this.sfw,
      haptic: this.preferences.haptic,
      notifications_buddies: this.preferences.notifications_buddies,
      notifications_messages: this.preferences.notifications_messages,
      notifications_streak: this.preferences.notifications_streak,
      favorites: this.favorites,
    };
  }

  // ponytail: `preserveExistingDoc` is a no-op kept for call-site compatibility --
  // the two upserts always merge (PostgREST merge-duplicates), never a full rewrite.
  async save(_preserveExistingDoc = true) {
    // Skip no-op saves on an existing user.
    if (!this.changesMade && this.id) {
      return;
    }
    this._assertCanSave();

    this.id = this.id || CurrentSession.authenticatedUser.uid;
    const written = _.cloneDeep(this.json);
    Logger.debug(`[SB-DB] Saving users/${this.id}`);

    // Diff each row against what we loaded and upsert only the changed columns.
    // This skips untouched fields entirely, so defaults left in memory by a failed
    // read can't clobber the real row, and a one-field edit writes one field.
    const publicRow = { ...this.toRow(), id: this.id, uid: this.id };
    const privateRow = this.toPrivateRow();
    const publicKeep = ["id", "uid", "migrated"];
    const privateKeep = ["id", "uid"];
    const publicWrite = changedColumns(
      publicRow,
      this._publicRowOriginal,
      publicKeep
    );
    const privateWrite = changedColumns(
      privateRow,
      this._privateRowOriginal,
      privateKeep
    );

    // TODO(perf): these writes bypass the query monitor, so a hung upsert won't
    // surface a toast the way monitored() reads do.
    if (hasPayload(publicWrite, publicKeep)) {
      const { error: pubErr } = await supabase
        .from("users")
        .upsert(publicWrite);
      if (pubErr) throw this._writeError("users", pubErr, "upsert");
    }
    if (hasPayload(privateWrite, privateKeep)) {
      const { error: privErr } = await supabase
        .from("users_private")
        .upsert(privateWrite);
      if (privErr) throw this._writeError("users_private", privErr, "upsert");
    }

    this._publicRowOriginal = publicRow;
    this._privateRowOriginal = privateRow;
    this.json_original = written;
    SupabaseDocument.resetCache();
  }

  async fetch() {
    // Read by the mock-aware uid so DEBUG_MOCK_UID emulation loads the target
    // user's public row (streak, last_tugged, totals) -- what the header shows.
    // No-op when the flag is empty (uid === id).
    const { data, error } = await supabase
      .from("users_public")
      .select("*")
      .eq("id", this.uid)
      .maybeSingle();
    if (error) throw error;
    return { id: data?.id ?? null, data };
  }

  async lastMethod() {
    const session = await TugSession.latest(this.uid);
    return session ? session.method : "";
  }

  get lastTugged() {
    return this.dates.lastTugged ? moment(this.dates.lastTugged) : null;
  }

  // True only when the user is flagged active AND their last activity is recent.
  // The bare `active` flag can linger when a session is started but never stopped
  // (user logged off and never returned), which would otherwise show a perpetual
  // "Tugging Now" pulse days later. Gate it on recency.
  get activeRecently() {
    return (
      this.active &&
      this.lastTugged !== null &&
      TimeUtils.inLast24Hours(this.lastTugged)
    );
  }

  set lastTugged(date) {
    const formattedDate = date
      ? date.format(TimeUtils.FullDateTimeString)
      : null;
    if (this.dates["lastTugged"] !== formattedDate) {
      if (
        this.lastTugged === null ||
        date === null ||
        this.lastTugged.isBefore(date)
      ) {
        this.dates["lastTugged"] = formattedDate;
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("userUpdated"));
        }
      }
    }
  }

  get levelCurrent() {
    if (_.get(this.level, ["current", "value"], null) === null) {
      return null;
    }
    return `${this.level.current.rci ? "R" : ""}CI-${_.get(
      this.level,
      ["current", "value"],
      0
    )}`;
  }

  set levelCurrent(string) {
    this.level.current = {
      rci: _.startsWith(string, "RCI"),
      value: Coverage.extractNumber(string),
    };
  }

  get levelStart() {
    if (_.get(this.level, ["start", "value"], null) === null) {
      return null;
    }
    return `${this.level.start.rci ? "R" : ""}CI-${_.get(
      this.level,
      ["start", "value"],
      0
    )}`;
  }

  set levelStart(string) {
    this.level.start = {
      rci: _.startsWith(string, "RCI"),
      value: Coverage.extractNumber(string),
    };
  }

  // Hydrate public fields from a `users_public` row (streak + totals folded in).
  hydratePublic(data) {
    const row = data || {};
    this.active = _.get(row, "active", false);
    this._above18 = _.get(row, "above18", false);
    this.bio = _.get(row, "bio", "") || "";
    this.deleted = _.get(row, "deleted", false);
    this.emoji = _.get(row, "emoji", null);
    this.extraSupportRequested = _.get(row, "extra_support_requested", false);
    this.hasPublicImages = _.get(row, "has_public_images", false);
    this.intact = _.get(row, "intact", false);
    this.incognito = _.get(row, "incognito", false);
    this.supporter = _.get(row, "supporter", false);

    this.dates.joined = TimeUtils.toMoment(_.get(row, "joined")) || moment();
    this.dates.started = TimeUtils.toMoment(_.get(row, "started"));
    this.dates.streakStart = TimeUtils.toMoment(_.get(row, "streak_start"));

    // Streak + last-tugged come from the streak_scores join in the view. Keep the
    // "floating" wall-clock convention the rest of the app uses (see refreshStreak).
    this._streak = _.get(row, "streak_days", 0);
    const lastTugged = _.get(row, "last_tugged");
    this.dates.lastTugged = lastTugged
      ? moment(
          moment(lastTugged)
            .tz(this.timezone || TimeUtils.getTimezone())
            .format(TimeUtils.FullDateTimeString)
        )
      : null;

    const parseLevel = (s) =>
      s
        ? { rci: _.startsWith(s, "RCI"), value: Coverage.extractNumber(s) }
        : { rci: false, value: null };
    this.level = {
      current: parseLevel(_.get(row, "level_current")),
      start: parseLevel(_.get(row, "level_start")),
    };

    this.kudos = _.get(row, "kudos", { sent: 0, received: 0 }) || {
      sent: 0,
      received: 0,
    };
    this.methods = _.get(row, "methods", {}) || {};
    this.location = _.get(row, "location", null);
    const display = _.get(row, "username", null);
    this.username = {
      display: display || null,
      standardized: display ? _.toLower(display) : null,
    };
    // Totals derived from snapshots (via the view). method_breakdown is sourced
    // from SupabaseSnapshots where charts need it. `methods` (method -> count,
    // the users.methods column) drives the UserCard category bar -- it lives at
    // stats.methods historically, so surface it here or every card's breakdown
    // renders empty.
    this.stats = {
      methods: this.methods,
      method_breakdown: {},
      days: _.get(row, "total_days", 0),
      duration: _.get(row, "total_duration", 0),
      sessions: _.get(row, "total_sessions", 0),
    };
  }

  // Hydrate owner-only fields from a `users_private` row.
  hydratePrivate(priv) {
    if (!priv) return;
    // above18 for the signed-in user is derived from their own birthday (private,
    // authoritative) rather than trusting the public flag, which can lag. Any save
    // then writes this corrected value to the public users.above18 for others.
    const birthday = _.get(priv, "birthday");
    if (birthday) {
      this._above18 = moment().year() - moment(birthday).year() >= 18;
    }
    this.spreadsheet = _.get(priv, "spreadsheet", null);
    this.timezone = _.get(priv, "timezone", null) || TimeUtils.getTimezone();
    this.streakDisabled = _.get(priv, "streak_disabled", false);
    this.sfw = _.get(priv, "sfw", false);
    this.dates.lastSync = TimeUtils.toMoment(_.get(priv, "last_sync"));
    const messages = _.get(priv, "notifications_messages", true);
    this.preferences = {
      notifications_buddies: _.get(priv, "notifications_buddies", true),
      notifications_messages: messages,
      notifications_streak: _.get(priv, "notifications_streak", true),
      // kudos merged into messages; keep in memory for any UI that still reads it.
      notifications_kudos: messages,
      haptic: _.get(priv, "haptic", true),
    };
    this.favorites = _.get(priv, "favorites", []) || [];
  }

  async load(loadSnapshot = false, data = null) {
    if (!data) {
      const fetchedData = await this.fetch();
      this.id = fetchedData.id ?? this.id;
      data = fetchedData.data;
    }

    this.hydratePublic(data);

    // Only the signed-in user can read their own users_private row (RLS).
    // Through the memoized query layer so the profile's several charts (each
    // loading the same user) collapse to one private read instead of N.
    const ownUid = CurrentSession.authenticatedUser?.uid;
    let privateRowExisted = false;
    let privateReadFailed = false;
    if (this.id && this.id === ownUid) {
      try {
        const rows = await SupabaseDocument.query(
          "users_private",
          (q) => q.eq("id", this.id),
          `private ${this.id}`
        );
        privateRowExisted = !!rows[0];
        this.hydratePrivate(rows[0]);
      } catch (error) {
        // Read failed (e.g. token/query timeout on a flaky or VPN connection).
        // We don't know the row's real contents, so baseline = the in-memory
        // defaults below: save() then writes only fields the user actually edits
        // this session, never the whole row -- which would clobber the real
        // spreadsheet/favorites/prefs with defaults.
        Logger.error("Error loading users_private:", error);
        privateReadFailed = true;
      }
    }

    if (loadSnapshot) {
      await this.snapshot.fetch();
    }

    // Baselines for save()'s column diff: our best knowledge of the DB rows.
    // null = row known absent -> save() writes the full row to create it. A
    // failed private read is NOT absent, so baseline = current (defaults) and
    // save() diffs against it (see the catch above).
    this._publicRowOriginal = data
      ? { ...this.toRow(), id: this.id, uid: this.id }
      : null;
    this._privateRowOriginal =
      privateReadFailed || privateRowExisted ? this.toPrivateRow() : null;
    this.saveCheckpoint();

    return Promise.resolve();
  }

  // Streak lives in Supabase (streak_scores view, derived from sessions). load()
  // already folds it in via users_public; this stays for callers that want to
  // re-source just the streak/last-tugged after a save without a full reload.
  async refreshStreak() {
    try {
      const rows = await SupabaseDocument.query(
        "streak_scores",
        (q) => q.eq("uid", this.uid),
        `streak ${this.uid}`
      );
      const row = rows[0];
      // Supabase last_tugged is treated as a user-tz "floating" wall clock; store
      // it in the same form so it isn't double-converted (see the long-standing
      // note on time-zone handling).
      const dbLastTugged = row?.last_tugged
        ? moment(
            moment(row.last_tugged)
              .tz(this.timezone || TimeUtils.getTimezone())
              .format(TimeUtils.FullDateTimeString)
          )
        : null;

      // Don't let a stale DB read clobber a fresher optimistic update. Starting a
      // tug sets lastTugged=now locally, but the session is in-progress and the
      // server streak (completed sessions only, see 0021) hasn't advanced yet --
      // applying it here would revert "streak safe" to "in danger" and leave the
      // header countdown running after the user already started tugging.
      const local = this.lastTugged;
      if (local && dbLastTugged && local.isAfter(dbLastTugged)) {
        return;
      }

      this._streak = _.get(row, "streak_days", 0);
      if (dbLastTugged) {
        this.dates.lastTugged = dbLastTugged;
      }
    } catch (error) {
      Logger.error("Error loading streak from Supabase:", error);
    }
  }

  get name() {
    return this.username.display;
  }

  get name_original() {
    return this.json_original.username.standardized;
  }

  set name(username) {
    this.username.display = _.trim(username);
    this.username.standardized = _.toLower(this.username.display);
  }

  get messagesDisabled() {
    return this.preferences.notifications_messages === false;
  }

  get notificationsDisabled() {
    return _.every([
      this.preferences.notifications_buddies === false,
      this.preferences.notifications_kudos === false,
    ]);
  }

  async pendingExport() {
    return TugSession.pendingExportCount(this.uid);
  }

  get profileLink() {
    if (this.name) {
      return `progress?user=${this.name}`;
    }
    return `progress?uid=${this.uid}`;
  }

  get profilePicture() {
    return bustProfileUrl(
      `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_CONFIG.storageBucket}/o/profile%2F${this.uid}?alt=media`
    );
  }

  static get profilePictureGeneric() {
    return `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_CONFIG.storageBucket}/o/profile%2Fanteater.webp?alt=media`;
  }

  async purgeImported() {
    const sessions = await TugSession.imported(this.uid);
    await Promise.all(sessions.map((session) => session.delete()));
    await this.save();
  }

  // Build a User synchronously from a users_public row (used by the list queries).
  static fromRow(row) {
    const user = new User(row.id);
    user.hydratePublic(row);
    user.saveCheckpoint();
    return user;
  }

  // Cap the community lists: the ordered top matters, an unbounded scan of
  // every active user does not (and it fans out into that many avatar probes).
  static COMMUNITY_LIMIT = 100;

  static async Recent() {
    const recently = TimeUtils.recently().format(TimeUtils.FullDateString);
    const rows = await SupabaseDocument.query(
      "users_public",
      (q) =>
        q
          .eq("incognito", false)
          .gte("last_tugged", recently)
          .order("streak_days", { ascending: false })
          .order("last_tugged", { ascending: false, nullsFirst: false })
          .limit(User.COMMUNITY_LIMIT),
      `query recent tugged users`
    );
    return rows.map((row) => User.fromRow(row));
  }

  static async New() {
    const recently = TimeUtils.recently().format(TimeUtils.FullDateString);
    const rows = await SupabaseDocument.query(
      "users_public",
      (q) =>
        q
          .gte("joined", recently)
          .lte("total_sessions", 0)
          .eq("incognito", false)
          .order("joined", { ascending: false })
          .limit(User.COMMUNITY_LIMIT),
      `query new users`
    );
    return rows.map((row) => User.fromRow(row));
  }

  // Batch-load many users in one query (feed hydration). `new User(uid)` sets
  // this.id = uid and users_public.id equals that uid, so .in("id", ids) is the
  // right column. Replaces N per-user load() round-trips.
  static async ByIds(ids) {
    const unique = _.uniq((ids || []).filter(Boolean));
    if (unique.length === 0) return [];
    // TODO(perf): the cache key joins every uid, so it can be huge (and never
    // reused across differently-ordered id sets). A sorted-hash or a short digest
    // would be smaller and order-insensitive.
    const rows = await SupabaseDocument.query(
      "users_public",
      (q) => q.in("id", unique),
      `users by ids ${unique.length}:${unique.join(",")}`
    );
    return rows.map((row) => User.fromRow(row));
  }

  static async WithLocation() {
    // Map pins need only identity + location + active state, not full profile
    // rows (bio, methods, totals, ...). Project to keep the payload small.
    const rows = await SupabaseDocument.query(
      "users_public",
      (q) => q.eq("incognito", false).not("location", "is", null),
      `query users with location`,
      "id,uid,username,location,active,last_tugged,incognito"
    );
    return rows
      .map((row) => User.fromRow(row))
      .filter((user) => user.location?.formatted);
  }

  static async SupportRequested() {
    const rows = await SupabaseDocument.query(
      "users_public",
      (q) =>
        q
          .eq("extra_support_requested", true)
          .order("joined", { ascending: false }),
      `query support users`
    );
    return rows.map((row) => User.fromRow(row));
  }

  setLevel(date, value, rci = false) {
    this.level[date].rci = rci;
    this.level[date].value = value;
  }

  /**
   * @param {moment.Moment} date
   */
  set started(date) {
    if (date.isBefore(this.dates.started)) {
      Logger.debug(`Identified new start date for user`);
      this.dates.started = date;
    }
  }

  get streak() {
    return Streak.calculateRealtimeState(
      this.lastTugged,
      this._streak,
      this.timezone
    ).streak;
  }

  get streakInDanger() {
    return Streak.calculateRealtimeState(
      this.lastTugged,
      this._streak,
      this.timezone
    ).streakInDanger;
  }

  setOptimisticStreak(value) {
    this._streak = value;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("userUpdated"));
    }
  }

  setOptimisticState(streak, lastTuggedDate) {
    this._streak = streak;
    if (lastTuggedDate) {
      this.lastTugged = lastTuggedDate;
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("userUpdated"));
    }
  }

  async updateLastTugged(knownLatestSession = null) {
    if (knownLatestSession) {
      if (
        knownLatestSession.inProgress ||
        knownLatestSession.json_original?.inProgress
      ) {
        this.active = knownLatestSession.inProgress;
      }
      this.lastTugged = knownLatestSession.end;
      this.timezone = knownLatestSession.timezone;
      // Local update only - do not persist to DB
      return;
    }

    // Update last tugged from DB
    const session = await TugSession.latest(this.id);
    if (!session) {
      // Only set to null if we don't have a local value that looks recent
      // This prevents a race condition where a just-saved session isn't found yet
      // but we have optimistically updated it locally.
      const localLastTugged = this.lastTugged;
      // If local value was updated within last 30 seconds, trust it over empty query results
      if (
        localLastTugged &&
        Math.abs(moment().diff(localLastTugged, "seconds")) < 30
      ) {
        Logger.debug("Keeping local lastTugged due to potential DB lag");
      } else {
        this.dates.lastTugged = null;
      }
    } else {
      this.active = session.inProgress;
      this.dates.lastTugged = session.end.format(TimeUtils.FullDateTimeString);
      this.timezone = session.timezone;
    }

    // Save changes
    // Local update only - do not persist to DB
    // this.save();
  }

  get uid() {
    // Dev override: emulate another account end-to-end (all uid-keyed reads --
    // sessions, history, feed, data-issues -- point at DEBUG_MOCK_UID). We
    // override uid, NOT id, so the real user doc still loads; writes will fail
    // RLS under a mock uid, so this is read-only emulation. "" = normal.
    return DEBUG_MOCK_UID || this.id;
  }

  async addFavorite(uid) {
    if (!this.favorites.includes(uid)) {
      this.favorites.push(uid);
      await this.save();
      // Tell any mounted UserCard (e.g. on the list page under a profile overlay)
      // to re-read its star, so favoriting in one place reflects everywhere.
      CurrentSession.notifyUserUpdate();
    }
  }

  async removeFavorite(uid) {
    const index = this.favorites.indexOf(uid);
    if (index > -1) {
      this.favorites.splice(index, 1);
      await this.save();
      CurrentSession.notifyUserUpdate();
    }
  }

  isFavorite(uid) {
    return this.favorites.includes(uid);
  }

  static async UsernameToUID(username) {
    const { data, error } = await supabase
      .from("users")
      .select("id")
      .ilike("username", _.trim(username))
      .limit(1);
    if (error) throw error;
    const uid = _.get(_.first(data), "id");
    if (!uid) {
      throw new Error(`User (${username}) not found`);
    }
    return uid;
  }

  async delete() {
    try {
      Logger.debug(`Marking user account for deletion: ${this.uid}`);

      // Set the deleted flag to true and save to Supabase
      this.deleted = true;
      await this.save();

      Logger.debug("User account marked for deletion successfully");
      return true;
    } catch (error) {
      Logger.error("Error marking user account for deletion:", error);
      throw error;
    }
  }

  get above18() {
    return this._above18 || false;
  }

  set above18(value) {
    this._above18 = value;
  }
}
